import { useContext, useEffect, useState } from "react";
import {
  Table,
  Tooltip,
  Card,
  DatePicker,
  Input,
  Button,
  message,
  Space,
  Select,
  notification,
  Drawer,
} from "antd";

import {
  SearchOutlined,
  ContactsTwoTone,
  PlayCircleTwoTone,
  CloudDownloadOutlined,
  LoadingOutlined,
  FileTwoTone,
  IdcardTwoTone,
  StopTwoTone,
  CheckCircleTwoTone,
} from "@ant-design/icons";

import { Context } from "../../context";
import { Fetch, Put } from "../../fetch";
import { convertIdToKey } from "../../utils";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

import "./styles.css";
import DownloadButton from "./DownloadButton";

const { Column } = Table;
const { RangePicker } = DatePicker;
const { Option } = Select;

const displayDateFormat = "YYYY-MM-DD";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const SubscriberReport = () => {
  const { token, apiUrl } = useContext(Context);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState([]);
  const [downloadLink, setDownloadLink] = useState(null);
  const [exportStarted, setExportStarted] = useState(false);
  const [totalPageViews, setTotalPageViews] = useState(0);
  const [totalUniqueVisitors, setTotalUniqueVisitors] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [playRate, setPlayRate] = useState(0);
  const [filters, setFilters] = useState({
    date: [dayjs().startOf("month"), dayjs()],
    searchText: null,
    spam: "leads",
  });
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
    hideOnSinglePage: true,
    showSizeChanger: false,
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [viewsData, setViewsData] = useState([]);
  const [userEmail, setUserEmail] = useState();
  const disabledDate = (current) => {
    return dayjs(current).isAfter(dayjs(), "day");
  };

  const rangePresets = [
    {
      label: "Yesterday",
      value: [dayjs().add(-1, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 3 Days",
      value: [dayjs().add(-3, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 7 Days",
      value: [dayjs().add(-7, "d"), dayjs().add(-1, "d")],
    },
    {
      label: "Last 30 Days",
      value: [dayjs().add(-30, "d"), dayjs().add(-1, "d")],
    },
  ];

  useEffect(() => {
    setLoading(true);
    let url, start_date, end_date, search_string, date_string;
    if (filters.date[0] & filters.date[1]) {
      start_date = dayjs(filters.date[0]).format(displayDateFormat);
      end_date = dayjs(filters.date[1]).format(displayDateFormat);
      date_string = `start_date=${start_date}&end_date=${end_date}`;
    } else {
      date_string = "";
    }
    if (filters.searchText) {
      search_string = `search_text=${filters.searchText}`;
    } else {
      search_string = "";
    }

    url = `${apiUrl}/subscribers?${date_string}&${search_string}&current=${pagination.current}&size=${pagination.pageSize}&spam=${filters.spam}`;

    Fetch(url, token, (result) => {
      const users = convertIdToKey(result.users);
      users.forEach((user) => {
        if (!user.email) {
          user.email = "-";
        }
        if (user.createdOn) {
          user.createdOn = dayjs(user.createdOn).format("DD MMM YYYY, hh:mm a");
        } else {
          user.createdOn = "-";
        }
        if (user.updatedOn) {
          user.updatedOn = dayjs(user.updatedOn).format("DD MMM YYYY, hh:mm a");
        } else {
          user.updatedOn = "-";
        }
      });
      setUsers(users);
      setPagination((p) => ({
        ...pagination,
        total: result.total,
      }));
      setLoading(false);
      setRefresh(false);
    });

    Fetch(
      `${apiUrl}/reports/user_conversion?${date_string}`,
      token,
      (result) => {
        if (result) {
          setTotalPageViews(result.page_views);
          setTotalViews(result.subscribed_users);
          setTotalSubscribers(result.total_subscribers);
          setPlayRate(result.conversion_rate);
          setTotalUniqueVisitors(result.unique_visitors);
        }
      }
    );
  }, [apiUrl, token, refresh, pagination.current, filters]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleExport = () => {
    setExportStarted(true);
    Fetch(`${apiUrl}/exports/users`, token, (result) => {
      if (result) {
        setDownloadLink(result);
        message.success("Files ready for download");
        setExportStarted(false);
      } else {
        message.error("Error Exporting Data");
        setExportStarted(false);
      }
    });
  };

  const toggleSpam = (record) => {
    let move_to, moved_to;
    if (record.spam === true) {
      move_to = "lead";
      moved_to = "Lead";
    } else if (record.spam === false || record.spam === undefined) {
      move_to = "spam";
      moved_to = "Spam";
    }
    Put(
      `${apiUrl}/subscribers/${record.key}?move_to=${move_to}`,
      token,
      {},
      (result) => {
        setRefresh(true);
        if (result) {
          notification.success({
            message: "Success",
            description: `User marked as ${moved_to}`,
          });
        } else {
          notification.error({
            message: "Something went wrong",
            description: `Please refresh and try again`,
          });
        }
      }
    );
  };

  const get_watch_time = (milliseconds) => {
    if (milliseconds === 0) {
      return " < 1";
    }
    let time = Math.round(milliseconds / 60000);
    if (time < 1) return " < 1";
    else if (time >= 1 && time <= 2) return "1";
    else return `${Math.round(milliseconds / 60000)}`;
  };

  const getViewsData = (email) => {
    Fetch(
      `${apiUrl}/views?filter_by=email&q=${encodeURIComponent(email)}`,
      token,
      (result) => {
        if (result.success === true) {
          setUserEmail(email);
          const views = result.data;
          views.forEach((view) => {
            view["view_start"] = dayjs(view.view_start).format(
              "DD MMM YYYY, hh:mm a"
            );
          });
          setViewsData(result.data);
          setDrawerVisible(true);
        }
      }
    );
  };

  return (
    <Card
      className="reports-wrapper"
      title={"Subscriber Reports"}
      extra={
        <div>
          {!downloadLink ? (
            <Button onClick={handleExport} disabled={downloadLink !== null}>
              {exportStarted ? <LoadingOutlined /> : <CloudDownloadOutlined />}
              Export Subscribers Data
            </Button>
          ) : (
            <Space direction="horizontal">
              <DownloadButton
                url={downloadLink.xlsx}
                fileName={"Subscribers.xlsx"}
                filetype={"Spreadsheet"}
              />
              <DownloadButton
                url={downloadLink.csv}
                fileName={"Subscribers.csv"}
                filetype={"CSV"}
              />
            </Space>
          )}
        </div>
      }
    >
      <div className="reports-filters-wrapper">
        <RangePicker
          defaultValue={[filters.date[0], filters.date[1]]}
          onChange={(_, datestring) => {
            console.log(datestring);
            setFilters({
              ...filters,
              date: [dayjs(datestring[0]), dayjs(datestring[1])],
            });
          }}
          allowClear={true}
          presets={rangePresets}
          disabledDate={disabledDate}
          style={{ width: "60%" }}
        />
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by email"
          onChange={(e) =>
            setFilters({ ...filters, searchText: e.target.value, spam: "all" })
          }
          style={{ marginLeft: "1%" }}
        />
      </div>
      <div className="overview-wrapper">
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <FileTwoTone twoToneColor={"orange"} />
            <div>
              <strong>Page Views : </strong>
            </div>
            <div>{totalPageViews}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <IdcardTwoTone twoToneColor={"red"} />
            <div>
              <strong>Unique Visitors : </strong>
            </div>
            <div>{totalUniqueVisitors}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <ContactsTwoTone twoToneColor="#e65251" />
            <div>
              <strong>Subscribed Users : </strong>
            </div>
            <div>{totalViews}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <ContactsTwoTone twoToneColor="#e65251" />
            <div>
              <strong>Total Subscribers : </strong>
            </div>
            <div>{totalSubscribers}</div>
          </Card>
        </div>
        <div style={{ width: "30%" }}>
          <Card className="info-card" bordered={false}>
            <PlayCircleTwoTone twoToneColor={"blue"} />
            <div>
              <strong>Conversion Rate : </strong>
            </div>
            <div>{playRate} %</div>
          </Card>
        </div>
      </div>
      <div className="leads-filter">
        <Select
          placeholder="Select Filter"
          defaultValue={"leads"}
          style={{
            width: "100%",
            maxWidth: "150px",
            float: "right",
            margin: "1%",
          }}
          value={filters.spam}
          onChange={(v) => setFilters({ ...filters, spam: v })}
        >
          <Option value="all">All Subscribers</Option>
          <Option value="leads">Leads</Option>
          <Option value="spam">Spam</Option>
        </Select>
      </div>
      <Table
        dataSource={users}
        loading={loading}
        size="small"
        pagination={pagination}
        onChange={handleTableChange}
      >
        <Column
          title="Email"
          align="left"
          dataIndex={"email"}
          width={"30%"}
          render={(_, record) => (
            <div style={{ paddingLeft: "2%" }}>
              <Button
                type="link"
                onClick={() => {
                  getViewsData(record.email);
                }}
              >
                {record.email}
              </Button>
            </div>
          )}
        />
        <Column title="Date Added" align="center" dataIndex={"createdOn"} />
        <Column
          title="UTM Source/Medium"
          align="center"
          render={(_, record) =>
            record.utm_source !== "NA" && record.utm_medium !== "NA"
              ? `${record.utm_source}/${record.utm_medium}`
              : "Direct/None"
          }
        />
        <Column
          title="Referrer"
          align="center"
          render={(_, record) =>
            record.referrer && record.referrer !== "NA" ? (
              record.referrer.length > 30 ? (
                <Tooltip title={record.referrer}>
                  <span>{`${record.referrer.slice(0, 30)}...`}</span>
                </Tooltip>
              ) : (
                record.referrer
              )
            ) : (
              "-"
            )
          }
        />
        <Column
          title="Actions"
          align="center"
          render={(_, record) => (
            <Tooltip title={record.spam ? "Mark as Lead" : "Mark as spam"}>
              <Button
                type="text"
                onClick={() => toggleSpam(record)}
                icon={
                  record.spam ? (
                    <CheckCircleTwoTone twoToneColor={"green"} />
                  ) : (
                    <StopTwoTone twoToneColor="red" />
                  )
                }
              />
            </Tooltip>
          )}
        />
      </Table>
      <Drawer
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setViewsData([]);
          setUserEmail();
        }}
        destroyOnClose={true}
        size="large"
        width={"1000px"}
        maskClosable={false}
        title={userEmail}
        keyboard={false}
        closable={true}
      >
        <Table dataSource={viewsData} pagination={false}>
          <Column title="View Start" align="center" dataIndex={"view_start"} />
          <Column title="Title" align="left" dataIndex={"content_title"} />
          <Column
            title="Watch Time (minutes)"
            align="center"
            dataIndex={"watch_time"}
            render={(_, record) =>
              record.watch_time ? get_watch_time(record.watch_time) : "NA"
            }
          />
        </Table>
      </Drawer>
    </Card>
  );
};

export default SubscriberReport;
